import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  getReminders,
  getMyReminders,
  createReminder,
  updateReminder,
  completeReminder,
  deleteReminder,
  getRemindersForEntity,
  reopenReminder,
  getOrganisationUsers,
  getBranchAdmins
} from 'lib/supabaseQueries';
import { useAuthContext } from 'context/authContext';
import useBranchPermission from './useBranchPermission';
import { toast } from 'react-toastify';

// Hook to fetch reminders with optional filters
export function useReminders(filters = {}) {
  const { organization, isInitialized } = useAuthContext();
  const { branchId } = useBranchPermission();

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['reminders', organization?.id, branchId, filters],
    queryFn: () => getReminders(organization.id, branchId, filters),
    enabled: isInitialized && !!organization?.id
  });

  return {
    reminders: data?.data || [],
    isLoading,
    error: error || data?.error,
    refetch
  };
}

// Hook to fetch reminders assigned to current user
// Uses RPC function that gets user from auth.uid() internally
export function useMyReminders() {
  const { isInitialized } = useAuthContext();

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['myReminders'],
    queryFn: () => getMyReminders(),
    enabled: isInitialized
  });

  return {
    reminders: data?.data || [],
    isLoading,
    error: error || data?.error,
    refetch
  };
}

// Hook to create a reminder
export function useCreateReminder() {
  const queryClient = useQueryClient();
  const { organization, selectedBranchId, user } = useAuthContext();
  const { branchId } = useBranchPermission();

  // Use selected branch, or fall back to user's actual branch from context
  const effectiveBranchId = branchId || selectedBranchId;

  const mutation = useMutation({
    mutationFn: async reminderData => {
      if (!effectiveBranchId) {
        throw new Error('Branch is required to create a reminder');
      }
      const result = await createReminder({
        ...reminderData,
        organisation_id: organization.id,
        branch_id: effectiveBranchId,
        created_by_user_id: reminderData.created_by_user_id || user?.id
      });
      if (result.error) {
        throw new Error(result.error);
      }
      return result;
    },
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['reminders'] }),
        queryClient.invalidateQueries({ queryKey: ['myReminders'] }),
        queryClient.invalidateQueries({ queryKey: ['pendingRemindersCount'] })
      ]);
      toast.success('Reminder created successfully');
    },
    onError: error => {
      console.error('Create reminder error:', error);
      toast.error('Failed to create reminder');
    }
  });

  return {
    createReminder: mutation.mutate,
    createReminderAsync: mutation.mutateAsync,
    isCreating: mutation.isPending,
    error: mutation.error
  };
}

// Hook to update a reminder
export function useUpdateReminder() {
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: async ({ id, data }) => {
      const result = await updateReminder(id, data);
      if (result.error) {
        throw new Error(result.error);
      }
      return result;
    },
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['reminders'] }),
        queryClient.invalidateQueries({ queryKey: ['myReminders'] }),
        queryClient.invalidateQueries({ queryKey: ['pendingRemindersCount'] })
      ]);
      toast.success('Reminder updated successfully');
    },
    onError: error => {
      console.error('Update reminder error:', error);
      toast.error('Failed to update reminder');
    }
  });

  return {
    updateReminder: mutation.mutate,
    updateReminderAsync: mutation.mutateAsync,
    isUpdating: mutation.isPending,
    error: mutation.error
  };
}

// Hook to mark a reminder as complete
export function useCompleteReminder() {
  const queryClient = useQueryClient();
  const { user } = useAuthContext();

  const mutation = useMutation({
    mutationFn: async id => {
      const result = await completeReminder(id, user?.id || null);
      if (result.error) {
        throw new Error(result.error);
      }
      return result;
    },
    onSuccess: async result => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['reminders'] }),
        queryClient.invalidateQueries({ queryKey: ['myReminders'] }),
        queryClient.invalidateQueries({ queryKey: ['pendingRemindersCount'] })
      ]);
      const reminder = result?.data;
      if (reminder?.created_by && reminder.created_by_user_id !== user?.id) {
        toast.success(
          `Reminder completed — ${reminder.created_by.first_name} ${reminder.created_by.last_name} has been notified`
        );
      } else {
        toast.success('Reminder completed');
      }
    },
    onError: error => {
      console.error('Complete reminder error:', error);
      toast.error('Failed to complete reminder');
    }
  });

  return {
    completeReminder: mutation.mutate,
    completeReminderAsync: mutation.mutateAsync,
    isCompleting: mutation.isPending,
    error: mutation.error
  };
}

// Hook to delete a reminder
export function useDeleteReminder() {
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: async id => {
      const result = await deleteReminder(id);
      if (result.error) {
        throw new Error(result.error);
      }
      return result;
    },
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['reminders'] }),
        queryClient.invalidateQueries({ queryKey: ['myReminders'] }),
        queryClient.invalidateQueries({ queryKey: ['pendingRemindersCount'] })
      ]);
      toast.success('Reminder deleted');
    },
    onError: error => {
      console.error('Delete reminder error:', error);
      toast.error('Failed to delete reminder');
    }
  });

  return {
    deleteReminder: mutation.mutate,
    deleteReminderAsync: mutation.mutateAsync,
    isDeleting: mutation.isPending,
    error: mutation.error
  };
}

// Hook to fetch organisation users (for assignment dropdown)
export function useOrganisationUsers() {
  const { organization, isInitialized } = useAuthContext();
  const { branchId } = useBranchPermission();

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['organisationUsers', organization?.id, branchId],
    queryFn: () => getOrganisationUsers(organization.id, branchId),
    enabled: isInitialized && !!organization?.id
  });

  return {
    users: data?.data || [],
    isLoading,
    error: error || data?.error,
    refetch
  };
}

// Hook to fetch branch admins (for auto-assigning reminders)
export function useBranchAdmins() {
  const { organization, isInitialized } = useAuthContext();
  const { branchId } = useBranchPermission();

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['branchAdmins', organization?.id, branchId],
    queryFn: () => getBranchAdmins(organization.id, branchId),
    enabled: isInitialized && !!organization?.id && !!branchId
  });

  return {
    admins: data?.data || [],
    isLoading,
    error: error || data?.error,
    refetch
  };
}

// Reminders linked to a donor or beneficiary (#28)
export function useRemindersForEntity(entityType, entityId) {
  const { isInitialized } = useAuthContext();
  const { data, isLoading, refetch } = useQuery({
    queryKey: ['reminders', 'entity', entityType, entityId],
    queryFn: () => getRemindersForEntity(entityType, entityId),
    enabled: isInitialized && !!entityType && !!entityId
  });
  return { reminders: data?.data || [], isLoading, refetch };
}

// Reopen a completed reminder and optionally reassign it (#32)
export function useReopenReminder() {
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: async ({ id, note, assignedToUserId }) => {
      const result = await reopenReminder(id, { note, assignedToUserId });
      if (result.error) throw new Error(result.error);
      return result;
    },
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['reminders'] }),
        queryClient.invalidateQueries({ queryKey: ['myReminders'] }),
        queryClient.invalidateQueries({ queryKey: ['pendingRemindersCount'] })
      ]);
      toast.success('Reminder reopened');
    },
    onError: error => {
      console.error('Reopen reminder error:', error);
      toast.error('Failed to reopen reminder');
    }
  });
  return {
    reopenReminder: mutation.mutate,
    reopenReminderAsync: mutation.mutateAsync,
    isReopening: mutation.isPending
  };
}
